import { Truck } from 'lucide-react';

import { COMMERCE } from '@/config/site';
import { useCartSummary } from '@/features/cart/useCart';
import { cn } from '@/lib/cn';
import { formatCents } from '@/lib/money';

import { BowlIcon } from '../common/icons';

/**
 * Progresso rumo à entrega grátis, desenhado como uma tigela enchendo.
 *
 * Uma barra comum comunicaria o mesmo número, mas a tigela diz "você está
 * alimentando seu pet" — e o concreto "faltam R$ 32,10" é o que de fato
 * aumenta o ticket médio.
 *
 * Fica oculto em modo "consulte o valor": dizer quanto falta para R$ 199
 * revela o subtotal por subtração, o que anularia a flag.
 */
export function FreeShippingMeter({ className }: { className?: string }) {
  const {
    subtotalCents,
    isEligibleForFreeShipping,
    amountToFreeShippingCents,
    freeShippingProgress,
  } = useCartSummary();

  if (!COMMERCE.showPrices) return null;
  if (subtotalCents === 0) return null;

  const percent = Math.round(freeShippingProgress * 100);

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-2xl border-2 border-sand-300 bg-cream-200 p-3',
        className,
      )}
    >
      <p className="flex items-center gap-2 text-sm font-semibold text-brown-800">
        {isEligibleForFreeShipping ? (
          <>
            <Truck className="size-4 shrink-0 text-whats-600" aria-hidden="true" />
            <span>
              Boa! Sua <strong className="text-whats-600">entrega é grátis</strong> 🎉
            </span>
          </>
        ) : (
          <>
            <BowlIcon className="size-4 shrink-0 text-caramel-500" />
            <span>
              Faltam{' '}
              <strong className="text-orange-600">{formatCents(amountToFreeShippingCents)}</strong>{' '}
              para a entrega grátis
            </span>
          </>
        )}
      </p>

      <div
        className="relative h-2.5 overflow-hidden rounded-full bg-sand-300"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Progresso para a entrega grátis"
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-500 ease-[var(--ease-out-soft)]',
            isEligibleForFreeShipping ? 'bg-whats-500' : 'bg-orange-500',
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
